import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site-config";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = `Einladung annehmen · ${SITE.brand.name}`;
    if (!token) { setError("Dieser Einladungslink ist unvollständig."); setChecking(false); return; }
    axios.post(`${API}/admin/verify-invite`, { token }, { timeout: 15000 })
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Diese Einladung ist ungültig oder abgelaufen."))
      .finally(() => setChecking(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Passwort muss mindestens 8 Zeichen haben."); return; }
    if (pw !== pw2) { toast.error("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/accept-invite`, { token, password: pw }, { timeout: 15000 });
      setDone(true);
      toast.success("Passwort gesetzt.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Fehlgeschlagen.";
      toast.error(typeof detail === "string" ? detail : "Fehlgeschlagen.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] flex items-center justify-center px-4" data-testid="accept-invite-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={72} showWordmark={false} /></div>
        <div className="glass-strong rounded-2xl p-7 sm:p-9">
          {checking ? (
            <div className="text-center text-[#C9C6B4] py-6"><Loader2 size={26} className="animate-spin mx-auto mb-3" /> Einladung wird geprüft…</div>
          ) : error ? (
            <div className="text-center" data-testid="invite-invalid">
              <ShieldAlert size={40} className="text-[#E27D7D] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-black tracking-tight mb-3">Einladung ungültig</h1>
              <p className="text-sm text-[#C9C6B4]">{error}</p>
              <button onClick={() => navigate("/admin/login")} className="mt-6 w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3 transition-all">Zum Login</button>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="invite-done">
              <CheckCircle2 size={40} className="text-[#B8CFA0] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-black tracking-tight mb-3">Willkommen an Bord</h1>
              <p className="text-sm text-[#C9C6B4]">Dein Passwort wurde gesetzt. Du kannst dich jetzt mit deinem Benutzernamen anmelden.</p>
              <button onClick={() => navigate("/admin/login")} className="mt-6 w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3 transition-all" data-testid="invite-to-login-btn">Zum Login</button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <UserPlus size={14} className="text-[#E8E5D0]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0]">Admin-Einladung</span>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2">Passwort festlegen</h1>
              <p className="text-sm text-[#C9C6B4] mb-1">Konto: <strong className="text-[#F3F1E8]">{invite?.username}</strong></p>
              <p className="text-sm text-[#C9C6B4] mb-6">{invite?.email}</p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
                <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="Passwort (min. 8 Zeichen)"
                  className="w-full bg-[#4E5A3B]/60 border border-white/15 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F]" data-testid="invite-pw1" />
                <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required placeholder="Passwort wiederholen"
                  className="w-full bg-[#4E5A3B]/60 border border-white/15 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F]" data-testid="invite-pw2" />
                <button type="submit" disabled={loading}
                  className="w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 transition-all" data-testid="invite-submit-btn">
                  {loading ? <><Loader2 className="animate-spin" size={16} /> Speichern…</> : <>Passwort festlegen</>}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AcceptInvite;
